<template>
  <div class="pc">
    <div class="card shadow-sm pc-card mb-4">
      <div class="pc-head">
        <div class="d-flex align-items-center" style="gap: 12px;">
          <div class="pc-head-icon"><i class="fe fe-pie-chart"></i></div>
          <div>
            <h6 class="mb-0 font-weight-bold">Cost of care</h6>
            <small class="text-muted">Encounter services plus approved claims{{ data ? ` · as of ${fmtDate(data.as_of)}` : "" }}</small>
          </div>
        </div>
        <div class="seg" role="tablist" aria-label="Period">
          <button v-for="p in periods" :key="p.key" type="button" role="tab" :aria-selected="period === p.key"
            class="seg-btn" :class="{ 'seg-btn--on': period === p.key }" @click="period = p.key">{{ p.label }}</button>
        </div>
      </div>

      <div v-if="loading" class="pc-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading costs…</div>
      <div v-else-if="error" class="pc-empty"><i class="fe fe-alert-triangle"></i> {{ error }}</div>
      <template v-else-if="data">
        <div class="pc-tiles">
          <div class="pc-tile pc-tile--total">
            <p class="pc-label">Total · {{ current.label }}</p>
            <div class="pc-value">{{ money(current.total) }}</div>
            <p class="pc-sub">{{ money(current.encounter_cost) }} encounters + {{ money(current.approved) }} approved claims</p>
          </div>
          <div class="pc-tile">
            <p class="pc-label"><span class="key key--enc"></span>Encounter services</p>
            <div class="pc-value pc-value--sm">{{ money(current.encounter_cost) }}</div>
            <p class="pc-sub">{{ current.encounters }} {{ current.encounters === 1 ? "encounter" : "encounters" }}</p>
          </div>
          <div class="pc-tile">
            <p class="pc-label"><span class="key key--claimed"></span>Claims claimed</p>
            <div class="pc-value pc-value--sm">{{ money(current.claimed) }}</div>
            <p class="pc-sub">{{ current.claims }} {{ current.claims === 1 ? "claim" : "claims" }} (drafts left out)</p>
          </div>
          <div class="pc-tile">
            <p class="pc-label"><span class="key key--approved"></span>Claims approved</p>
            <div class="pc-value pc-value--sm">{{ money(current.approved) }}</div>
            <p class="pc-sub">{{ current.claimed ? Math.round((100 * current.approved) / current.claimed) + "% of claimed" : "—" }}</p>
          </div>
        </div>

        <div class="pc-chart">
          <div class="d-flex flex-wrap align-items-center justify-content-between mb-1" style="gap: 8px;">
            <span class="font-weight-bold small text-uppercase text-muted">Spending over time</span>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <div class="seg seg--sm" role="tablist" aria-label="Range">
                <button v-for="r in ranges" :key="r.key" type="button" class="seg-btn" :class="{ 'seg-btn--on': range === r.key }" @click="range = r.key">{{ r.label }}</button>
              </div>
              <div class="seg seg--sm" role="tablist" aria-label="View">
                <button type="button" class="seg-btn" :class="{ 'seg-btn--on': !cumulative }" @click="cumulative = false">Monthly</button>
                <button type="button" class="seg-btn" :class="{ 'seg-btn--on': cumulative }" @click="cumulative = true">Running total</button>
              </div>
            </div>
          </div>
          <apexchart v-if="months.length" :type="cumulative ? 'area' : 'bar'" height="290" :options="chartOptions" :series="series" :key="chartKey" />
          <div v-else class="pc-empty py-4"><i class="fe fe-bar-chart-2"></i> No costs recorded yet.</div>
        </div>

        <div class="row no-gutters pc-lists" v-if="data.top_items.length || data.facilities.length">
          <div class="col-md-6 pc-list">
            <p class="pc-label mb-2">What cost the most (all time)</p>
            <div v-for="i in data.top_items" :key="i.kind + i.id" class="bar-row">
              <div class="d-flex justify-content-between small" style="gap: 8px;">
                <span class="text-truncate"><i :class="i.kind === 'drug' ? 'fe fe-package' : 'fe fe-clipboard'" class="text-muted mr-1"></i>{{ i.name }}</span>
                <span class="text-nowrap font-weight-bold">{{ money(i.amount) }} <span class="text-muted font-weight-normal">· {{ i.times }}×</span></span>
              </div>
              <div class="bar"><span :style="{ width: pct(i.amount, topItemMax) }"></span></div>
            </div>
            <div v-if="!data.top_items.length" class="small text-muted">None recorded.</div>
          </div>
          <div class="col-md-6 pc-list">
            <p class="pc-label mb-2">Where (all time)</p>
            <div v-for="f in data.facilities" :key="f.id" class="bar-row">
              <div class="d-flex justify-content-between small" style="gap: 8px;">
                <span class="text-truncate"><i class="fe fe-home text-muted mr-1"></i>{{ f.name }}</span>
                <span class="text-nowrap font-weight-bold">{{ money(f.encounter_cost + f.approved) }}</span>
              </div>
              <div class="bar bar--split">
                <span class="enc" :style="{ width: pct(f.encounter_cost, facilityMax) }" :title="`Encounter services ${money(f.encounter_cost)}`"></span>
                <span class="appr" :style="{ width: pct(f.approved, facilityMax) }" :title="`Approved claims ${money(f.approved)}`"></span>
              </div>
            </div>
            <div v-if="!data.facilities.length" class="small text-muted">None recorded.</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const COLORS = { enc: "#2563eb", approved: "#7c3aed", claimed: "#94a3b8" };

/** Cost of care of one enrollee (GET patients/{type}/{id}/costs): periods, monthly chart, top items, facilities. */
export default {
  props: {
    type: { type: String, required: true }, // "client" for a principal, anything else a dependant
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      data: null, loading: false, error: "", period: "this_year", range: "12", cumulative: false,
      periods: [{ key: "this_month", label: "This month" }, { key: "this_year", label: "This year" }, { key: "all_time", label: "All time" }],
      ranges: [{ key: "12", label: "12 months" }, { key: "24", label: "24 months" }, { key: "all", label: "All" }],
    };
  },
  computed: {
    current() { return this.data.periods[this.period]; },
    months() {
      const all = this.data.monthly || [];
      return this.range === "all" ? all : all.slice(-Number(this.range));
    },
    series() {
      const pick = (key) => this.months.map((m) => m[key]);
      const run = (arr) => { let t = 0; return arr.map((v) => Math.round((t += v) * 100) / 100); };
      const f = this.cumulative ? run : (a) => a;
      const s = [
        { name: "Encounter services", type: this.cumulative ? "area" : "column", data: f(pick("encounter_cost")) },
        { name: "Claims approved", type: this.cumulative ? "area" : "column", data: f(pick("approved")) },
        { name: "Claims claimed", type: "line", data: f(pick("claimed")) },
      ];
      return s;
    },
    chartKey() { return `${this.range}-${this.cumulative}-${this.months.length}`; },
    chartOptions() {
      const money = this.money;
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
        colors: [COLORS.enc, COLORS.approved, COLORS.claimed],
        stroke: { width: this.cumulative ? [2, 2, 2] : [0, 0, 2], curve: "smooth", dashArray: [0, 0, 5] },
        fill: { opacity: this.cumulative ? [0.18, 0.18, 1] : [1, 1, 1] },
        plotOptions: { bar: { columnWidth: this.months.length > 18 ? "75%" : "55%", borderRadius: 4, borderRadiusApplication: "end" } },
        markers: { size: this.cumulative ? 0 : 4, strokeWidth: 2, strokeColors: "#fff" },
        dataLabels: { enabled: true },
        xaxis: { categories: this.months.map((m) => m.label), labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: "#64748b" } }, axisBorder: { color: "#e2e8f0" } },
        yaxis: { labels: { formatter: (v) => money(v, true), style: { colors: "#64748b" } } },
        grid: { borderColor: "#eef2f7", strokeDashArray: 3 },
        legend: { position: "top", horizontalAlign: "left", fontWeight: 600, labels: { colors: "#334155" } },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => money(v) } },
      };
    },
    topItemMax() { return Math.max(1, ...this.data.top_items.map((i) => i.amount)); },
    facilityMax() { return Math.max(1, ...this.data.facilities.map((f) => f.encounter_cost + f.approved)); },
  },
  watch: {
    id() { this.load(); },
    type() { this.load(); },
  },
  created() { this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.axios.get(`/api/v1/auth/patients/${this.type}/${this.id}/costs`)
        .then((res) => { this.data = res.data.data; this.$emit("loaded", this.data); })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the costs."; })
        .finally(() => { this.loading = false; });
    },
    money(v, short) {
      const n = Number(v) || 0;
      if (short && Math.abs(n) >= 1e6) return "₦" + (n / 1e6).toFixed(1).replace(/\.0$/, "") + "m";
      if (short && Math.abs(n) >= 1e3) return "₦" + (n / 1e3).toFixed(0) + "k";
      return "₦" + Math.round(n).toLocaleString("en-US");
    },
    pct(v, max) { return Math.max(v > 0 ? 2 : 0, (100 * v) / max) + "%"; },
    fmtDate(d) { return moment(d).format("D MMM YYYY"); },
  },
};
</script>

<style scoped>
.pc-card { border-radius: 14px; border: 1px solid #e9ecef; overflow: hidden; }
.pc-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid #eef2f7; }
.pc-head-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, #1e3a5f, #2563eb); color: #fff; display: flex; align-items: center; justify-content: center; }
.seg { display: inline-flex; background: #f1f5f9; border-radius: 10px; padding: 3px; gap: 2px; }
.seg-btn { border: 0; background: transparent; color: #475569; font-weight: 600; font-size: 0.8rem; padding: 6px 12px; border-radius: 8px; cursor: pointer; }
.seg--sm .seg-btn { font-size: 0.72rem; padding: 4px 10px; }
.seg-btn--on { background: #fff; color: #1e3a5f; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12); }
.seg-btn:focus-visible { outline: 2px solid #2563eb; outline-offset: 1px; }
.pc-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; padding: 16px 20px 4px; }
@media (max-width: 991px) { .pc-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.pc-tile { border: 1px solid #eef2f7; border-radius: 12px; padding: 12px 14px; background: #fff; min-width: 0; }
.pc-tile--total { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 70%, #7c3aed 100%); border: 0; color: #fff; }
.pc-tile--total .pc-label, .pc-tile--total .pc-sub { color: rgba(255, 255, 255, 0.8); }
.pc-tile--total .pc-value { color: #fff; }
.pc-label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 700; margin-bottom: 4px; display: flex; align-items: center; gap: 6px; }
.pc-value { font-size: 1.5rem; font-weight: 800; color: #0f172a; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-value--sm { font-size: 1.15rem; }
.pc-sub { font-size: 0.74rem; color: #64748b; margin: 2px 0 0; }
.key { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
.key--enc { background: #2563eb; }
.key--approved { background: #7c3aed; }
.key--claimed { background: transparent; border: 2px dashed #94a3b8; }
.pc-chart { padding: 12px 20px 4px; }
.pc-lists { border-top: 1px solid #eef2f7; }
.pc-list { padding: 14px 20px; }
.pc-list + .pc-list { border-left: 1px solid #eef2f7; }
@media (max-width: 767px) { .pc-list + .pc-list { border-left: 0; border-top: 1px solid #eef2f7; } }
.bar-row { margin-bottom: 10px; }
.bar { height: 6px; border-radius: 4px; background: #f1f5f9; margin-top: 4px; overflow: hidden; display: flex; gap: 2px; }
.bar span { display: block; height: 100%; border-radius: 4px; background: #2563eb; }
.bar--split .appr { background: #7c3aed; }
.pc-empty { text-align: center; color: #94a3b8; padding: 32px 16px; }
</style>
